import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { AuthShell, Field } from "./AuthShell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/common/PasswordInput";
import { api, apiError } from "@/lib/api";

export function ResetUnavailable() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="reset-unavailable">
      <ShieldCheck className="h-6 w-6 text-emerald-500" />
      <p className="mt-3 font-medium text-slate-800">Self-service password reset isn't available yet.</p>
      <p className="mt-2 text-sm text-slate-500">
        Restaurant owners: please contact your platform administrator to reset your password.
        Staff: please ask your restaurant owner.
      </p>
    </div>
  );
}

export default function Forgot() {
  const nav = useNavigate();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [token, setToken] = useState("");
  const [pw, setPw] = useState({ password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(0);

  useEffect(() => {
    if (wait <= 0) return undefined;
    const t = setTimeout(() => setWait(wait - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const sendCode = (e) => {
    e?.preventDefault();
    run(async () => {
      const { data } = await api.post("/auth/forgot", { email });
      if (!data.self_service_enabled) return setStep("unavailable");
      toast.success(data.message);
      setStep("otp");
      setWait(60);
    });
  };
  const verify = (e) => {
    e.preventDefault();
    run(async () => { setToken((await api.post("/auth/verify-otp", { email, otp })).data.reset_token); setStep("password"); });
  };
  const reset = (e) => {
    e.preventDefault();
    if (pw.password.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw.password !== pw.confirm) return toast.error("Passwords do not match");
    run(async () => { toast.success((await api.post("/auth/reset", { token, password: pw.password })).data.message); nav("/login"); });
  };
  const submitBtn = (label, id) => (
    <Button type="submit" disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-600" data-testid={id}>
      {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{label}
    </Button>
  );

  return (
    <AuthShell
      title="Reset password"
      footer={<Link to="/login" className="font-semibold text-emerald-600 hover:underline" data-testid="back-to-login">Back to sign in</Link>}
    >
      {step === "unavailable" && <ResetUnavailable />}
      {step === "email" && (
        <form onSubmit={sendCode} className="space-y-4" data-testid="forgot-email-form">
          <Field label="Registered email"><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" /></Field>
          {submitBtn("Send reset code", "forgot-send-code-btn")}
        </form>
      )}
      {step === "otp" && (
        <form onSubmit={verify} className="space-y-4" data-testid="forgot-otp-form">
          <p className="text-sm text-slate-500">Enter the 6-digit code sent to <b>{email}</b>.</p>
          <Field label="Reset code"><Input required inputMode="numeric" maxLength={6} pattern="\d{6}" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} data-testid="forgot-otp-input" /></Field>
          {submitBtn("Verify code", "forgot-verify-btn")}
          <button type="button" disabled={wait > 0 || busy} onClick={sendCode} className="w-full text-sm text-emerald-600 disabled:text-slate-400" data-testid="forgot-resend-btn">
            {wait > 0 ? `Resend code in ${wait}s` : "Resend code"}
          </button>
        </form>
      )}
      {step === "password" && (
        <form onSubmit={reset} className="space-y-4" data-testid="forgot-password-form">
          <Field label="New password"><PasswordInput required value={pw.password} onChange={(e) => setPw({ ...pw, password: e.target.value })} placeholder="At least 8 characters" data-testid="forgot-new-password" /></Field>
          <Field label="Confirm new password"><PasswordInput required value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="forgot-confirm-password" /></Field>
          {submitBtn("Set new password", "forgot-reset-btn")}
        </form>
      )}
    </AuthShell>
  );
}
